import { useEffect, useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router';

// mui
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button } from '@mui/material';

// redux
import { useSelector } from 'react-redux';

// notistack closeSnackbar
import { closeSnackbar } from 'notistack';

// dayjs
import dayjs from 'dayjs';

// project imports
import useLogoutAndRedirect from 'hooks/useLogoutAndRedirect';
import { setLastRecordedActivity } from 'utils/logout';
import navigateStates from 'constants/navigateStates';
import { DEFAULT_INACTIVITY_TIMEOUT, INACTIVITY_DIALOG_GRACE_PERIOD } from 'constants/auth';

import { runBeforeLogoutHandlers } from 'utils/beforeLogout';

// events that reset timer
const events = ['load', 'mousedown', 'click', 'scroll', 'keydown', 'touchstart', 'touchend'];

// set the time value for inactivity (in milliseconds)
const inactivityTimeout = Number(import.meta.env.VITE_APP_INACTIVITY_TIMEOUT) || DEFAULT_INACTIVITY_TIMEOUT;

// set the time value for the "stay logged in?" grace window (in milliseconds)
const inactivityDialogGracePeriod = Number(import.meta.env.VITE_APP_INACTIVITY_DIALOG_GRACE_PERIOD) || INACTIVITY_DIALOG_GRACE_PERIOD;

// message to show on login page after logout due to inactivity
const inactivityMessage = 'You have been automatically logged out for security purposes, please log back in.';

const AppLogout = ({ children }) => {
  const { isLoggedIn } = useSelector((state) => state.user);
  const location = useLocation();
  const logoutAndRedirect = useLogoutAndRedirect();

  const [showInactivityDialog, setShowInactivityDialog] = useState(false);
  const isDialogOpenRef = useRef(false);

  // logs out user
  const logoutAction = useCallback(async () => {
    await logoutAndRedirect();
  }, [logoutAndRedirect]);

  // closes all notistack bars when route changes
  useEffect(() => {
    if (location.state !== navigateStates.FirstRedirect) {
      closeSnackbar();
    }
  }, [location]);

  const handleStayLoggedIn = useCallback(() => {
    setLastRecordedActivity();
    localStorage.removeItem('inactivityMessage');
    isDialogOpenRef.current = false;
    setShowInactivityDialog(false);
  }, []);

  const handleInactivityLogout = useCallback(async () => {
    await runBeforeLogoutHandlers(); // wait for saves to finish before proceeding
    localStorage.removeItem('lastRecordedActivity');
    localStorage.setItem('inactivityMessage', inactivityMessage);
    isDialogOpenRef.current = false;
    setShowInactivityDialog(false);
    await logoutAction();
  }, [logoutAction]);

  useEffect(() => {
    let checkInactivity;

    const handleAutoLogout = () => {
      if (!isLoggedIn) {
        return;
      }

      const lastRecordedActivity = localStorage.getItem('lastRecordedActivity');

      if (!lastRecordedActivity) {
        return;
      }

      const inactiveFor = dayjs().diff(dayjs(lastRecordedActivity), 'millisecond');

      if (inactiveFor < inactivityTimeout) {
        return;
      }

      if (inactiveFor >= inactivityTimeout + inactivityDialogGracePeriod) {
        handleInactivityLogout();
        return;
      }

      if (!isDialogOpenRef.current) {
        isDialogOpenRef.current = true;
        setShowInactivityDialog(true);
      }
    };

    handleAutoLogout();

    if (isLoggedIn) {
      // each time any of the events is triggered, i.e on mouse click, scroll etc, a timestamp of the event is saved in local storage
      Object.values(events).forEach((item) => {
        window.addEventListener(item, setLastRecordedActivity);
      });

      // every 10 seconds, the last activity timestamp is checked against the allowed inactivity timeout
      // if it exceeds the timeout, the user is automatically logged out
      checkInactivity = setInterval(handleAutoLogout, 10000);
    }

    return () => {
      Object.values(events).forEach((item) => {
        window.removeEventListener(item, setLastRecordedActivity);
      });

      if (checkInactivity) {
        clearInterval(checkInactivity);
      }
    };
  }, [isLoggedIn, logoutAction, handleInactivityLogout]);

  // ensure the inactivity dialog is dismissed if the user gets logged out
  // through any other path (e.g. auth token refresh failure, logout in another tab)
  useEffect(() => {
    if (!isLoggedIn) {
      isDialogOpenRef.current = false;
      setShowInactivityDialog(false);
    }
  }, [isLoggedIn]);

  // This triggers the user to logout from the app across all tabs within a browser whenever 'isLoggedOut' value is changed to FALSE
  // Currently, there are two workflows that triggers this:
  // -- User is logged in and clicks logout at the Profile Section in the app
  // -- User is at the login page and selects an authentication method (Microsoft or Google), manifesting intention to login again
  useEffect(() => {
    const loginEventListener = (e) => {
      if (e.key === 'isLoggedIn' && e.newValue === 'false') {
        logoutAction();
      }
    };

    window.addEventListener('storage', loginEventListener);

    return () => {
      window.removeEventListener('storage', loginEventListener);
    };
  }, [logoutAction]);

  return (
    <>
      {children}
      <Dialog
        open={showInactivityDialog}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          handleStayLoggedIn();
        }}
        disableEscapeKeyDown
      >
        <DialogTitle>Session Timeout</DialogTitle>
        <DialogContent>
          <DialogContentText>You have been inactive for a while. Would you like to stay logged in?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleInactivityLogout}>Log Out</Button>
          <Button color="primary" variant="contained" onClick={handleStayLoggedIn}>
            Stay Logged In
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AppLogout;
